import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { NavLink } from "react-router";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback } from "react";
import {
  BALANCE_ACTIVITY_HISTORY_PATH,
  scrollToActivityHistory,
} from "@/shared/lib/activity-history-anchor";
import { resetStates } from "@/shared/lib/utils";
import { useAppLogout } from "@/shared/lib/useAppLogout";
import { useTranslation } from "react-i18next";
import {
  getLandingCardsCatalogUrl,
  getLandingSpreadsCatalogUrl,
  LANDING_CONTACTS_URL,
} from "@/shared/config/marketingUrls";
import { normalizeAppLocale } from "@/shared/lib/appLocale";
import { useAuthRoles } from "@/entities/partner";

const mobileNavigationItemClass =
  "md:hidden hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]";

export const UserMenu = () => {
  const { user } = useKindeAuth();
  const { isAdmin, isPartner } = useAuthRoles();
  const handleLogout = useAppLogout();
  const { t } = useTranslation("userProfile");
  const { t: tLayout, i18n } = useTranslation("layout");
  const appLocale = normalizeAppLocale(i18n.resolvedLanguage ?? i18n.language);
  const spreadsCatalogUrl = getLandingSpreadsCatalogUrl(appLocale);
  const cardsCatalogUrl = getLandingCardsCatalogUrl(appLocale);

  const handleMyReadingsClick = useCallback(() => {
    resetStates();
    if (window.location.pathname === "/balance") {
      scrollToActivityHistory();
    }
  }, []);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="p-3 flex gap-3 items-center justify-center glass-effect relative rounded-[20px] cursor-pointer aspect-square">
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect y="2" width="12" height="2" rx="1" fill="white" />
          <rect y="8" width="12" height="2" rx="1" fill="white" />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#39326e] p-2">
        <DropdownMenuLabel className="mb-4 text-[#D4B9ED] font-heading font-semibold text-[32px]">
          {user?.givenName} {user?.familyName}
          <p className="text-[#C1BFCA] font-body font-normal text-[16px]">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <NavLink to="/profile" className="cursor-pointer text-white flex items-center gap-3">
            <img src="/icons/profile.svg" alt="" className="w-5 h-5 shrink-0" />
            {t("menu.editProfile")}
          </NavLink>
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
            <NavLink to="/admin/partners" className="cursor-pointer text-white flex items-center gap-3">
              <img src="/icons/handshake.svg" alt="" className="w-5 h-5 shrink-0" />
              {t("menu.partners")}
            </NavLink>
          </DropdownMenuItem>
        ) : null}
        {isPartner ? (
          <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
            <NavLink to="/partner" className="cursor-pointer text-white flex items-center gap-3">
              <img src="/icons/handshake.svg" alt="" className="w-5 h-5 shrink-0" />
              {t("menu.partnerCabinet")}
            </NavLink>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <NavLink
            to={BALANCE_ACTIVITY_HISTORY_PATH}
            onClick={handleMyReadingsClick}
            className="cursor-pointer text-white flex items-center gap-3"
          >
            <img src="/icons/readings.svg" alt="" className="w-5 h-5 shrink-0" />
            {tLayout("navigation.myReadings")}
          </NavLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <a href={LANDING_CONTACTS_URL} className="cursor-pointer text-white flex items-center gap-3">
            <img src="/icons/support.svg" alt="" className="w-5 h-5 shrink-0" />
            {t("menu.support")}
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <NavLink
            to="/daily"
            onClick={resetStates}
            className="cursor-pointer text-white flex items-center gap-3"
          >
            <img src="/icons/daily.svg" alt="" className="w-5 h-5 shrink-0" />
            {tLayout("navigation.cardOfTheDay")}
          </NavLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className={mobileNavigationItemClass}>
          <a href={spreadsCatalogUrl} className="cursor-pointer text-white">
            <img src="/icons/spreads.svg" alt="" className="w-5 h-5 shrink-0" />
            {tLayout("navigation.spreadsCatalog")}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className={mobileNavigationItemClass}>
          <a href={cardsCatalogUrl} className="cursor-pointer text-white">
            <img src="/icons/cards.svg" alt="" className="w-5 h-5 shrink-0" />
            {tLayout("navigation.cardsCatalog")}
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380] cursor-pointer text-white"
          onSelect={handleLogout}
        >
          <img src="/icons/logout.svg" alt="" className="w-5 h-5 shrink-0" />
          {t("logout.button")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <NavLink to="/privacy-policy" className="cursor-pointer text-white/70">
            {t("legal.privacyPolicy")}
          </NavLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="mt-0 hover:bg-[#4f487f] focus:bg-[#4f487f] active:bg-[#4b4380]">
          <NavLink to="/terms-of-use" className="cursor-pointer text-white/70">
            {t("legal.termsOfUse")}
          </NavLink>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
